/* eslint-disable react-hooks/rules-of-hooks */
import {
  Circle2d,
  EASINGS,
  Editor,
  SVGContainer,
  ShapeUtil,
  TLDrawShapeSegment,
  TLResizeInfo,
  Vec,
  VecLike,
  VecModel,
  highlightShapeProps,
  lerp,
  rng,
  useValue,
  FONT_SIZES,
  Polygon2d,
  useDefaultColorTheme,
  TLBaseShape,
  TLDefaultColorStyle,
  TLDefaultFillStyle,
  TLDefaultDashStyle,
  TLDefaultSizeStyle,
  RecordProps,
  T,
} from "@tldraw/tldraw";
import React, { useEffect, useState } from "react";
import { getFreehandOptions, getHighlightFreehandSettings, getPointsFromSegments, StrokeOptions } from "./getPath";
import { getSvgPathFromStrokePoints } from "../../newSVG";
import { last } from "./DrawShapeUtil";
import { getStrokePoints } from "../../newGeoStrokePoints";
import { StrokePoint } from "../../newTypes";
import { mixColors } from "../CustomCloudUtil";
import { getDashArray } from "../../new-geo-shape-helpers";

/** @public */
export interface HighlightShapeOptions {
  /**
   * The maximum number of points in a line before the customDraw tool will begin a new shape.
   * A higher number will lead to poor performance while drawing very long lines.
   */
  readonly maxPointsPerShape: number;
  readonly underlayOpacity: number;
  readonly overlayOpacity: number;
}

export type TLHighlightShapeUtilProps = TLBaseShape<
  "customDraw",
  {
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    color: TLDefaultColorStyle;
    fill: TLDefaultFillStyle;
    dash: TLDefaultDashStyle;
    size: TLDefaultSizeStyle;
    segments: TLDrawShapeSegment[];
    isComplete: boolean;
    isClosed: boolean;
    isPen: boolean;
    scale: number;
    weight?: number;
    outlineColor: string;
    transparency: number;
    strokeDasharray?: string;
    strokeWidth: number;
    filterBlur?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isScale?: boolean;
    isShowIndicator: boolean;
  }
>;

/** @public */
export class HighlightShapeUtil extends ShapeUtil<TLHighlightShapeUtilProps> {
  // getGeometry(shape: TLHighlightShapeUtilProps): Geometry2d {
  //   const points = getPointsFromSegments(shape.props.segments);

  //   const sw = (STROKE_SIZES[shape.props.size] + 1) * shape.props.scale;

  //   // A dot
  //   if (shape.props.segments.length === 1) {
  //     const box = Box.FromPoints(points);
  //     if (box.width < sw * 2 && box.height < sw * 2) {
  //       return new Circle2d({
  //         x: -sw,
  //         y: -sw,
  //         radius: sw,
  //         isFilled: true,
  //       });
  //     }
  //   }

  //   const strokePoints = getStrokePoints(points, getFreehandOptions(shape.props, sw, shape.props.isPen, true)).map(
  //     (p: any) => p.point
  //   );

  //   // A closed draw stroke
  //   if (shape.props.isClosed) {
  //     return new Polygon2d({
  //       points: strokePoints,
  //       isFilled: shape.props.fill !== "none",
  //     });
  //   }

  //   // An open draw stroke
  //   return new Polyline2d({
  //     points: strokePoints,
  //   });
  // }

  static type = "customHighlight" as const;
  static props: RecordProps<TLHighlightShapeUtilProps> = {
    ...highlightShapeProps,
    colorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    outlineColor: T.string,
    dash: T.any as unknown as RecordProps<TLHighlightShapeUtilProps>["dash"],
    transparency: T.number,
    weight: T.optional(T.number),
    strokeDasharray: T.optional(T.string),
    strokeWidth: T.number,
    filterBlur: T.optional(T.number),
    borderWidth: T.optional(T.number),
    borderColor: T.optional(T.string),
    borderSolid: T.any as unknown as RecordProps<TLHighlightShapeUtilProps>["borderSolid"],
    borderTopStyle: T.optional(T.string),
    borderBottomStyle: T.optional(T.string),
    borderLeftStyle: T.optional(T.string),
    borderRightStyle: T.optional(T.string),
    isShowBorderTop: T.optional(T.boolean),
    isShowBorderBottom: T.optional(T.boolean),
    isShowBorderLeft: T.optional(T.boolean),
    isShowBorderRight: T.optional(T.boolean),
    isCreatedFilterBlur: T.optional(T.boolean),
    filterBlurValueWhenHidden: T.optional(T.number),
    outShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    inShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    previousBorderColor: T.optional(T.string),
    isCreatedBorderItem: T.optional(T.boolean),

    outlineColorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    isHiddenLayerBlur: T.optional(T.boolean),
    isHiddenBorder: T.optional(T.boolean),
    isScale: T.optional(T.boolean),
    isShowIndicator: T.boolean,
    fill: T.any as unknown as RecordProps<TLHighlightShapeUtilProps>["fill"],
    isClosed: T.boolean,
  };

  options: HighlightShapeOptions = {
    maxPointsPerShape: 600,
    underlayOpacity: 0.82,
    overlayOpacity: 0.35,
  };

  hideResizeHandles(shape: TLHighlightShapeUtilProps) {
    return getIsDot(shape);
  }
  hideRotateHandle(shape: TLHighlightShapeUtilProps) {
    return getIsDot(shape);
  }
  hideSelectionBoundsFg(shape: TLHighlightShapeUtilProps) {
    return getIsDot(shape);
  }

  getDefaultProps(): TLHighlightShapeUtilProps["props"] {
    return {
      segments: [],
      color: "black" as TLDefaultColorStyle,
      size: "m" as TLDefaultSizeStyle,
      isComplete: false,
      isPen: false,
      scale: 1,
      isClosed: false,
      outlineColor: "#000000",
      transparency: 0.35,
      strokeWidth: 0,
      filterBlur: 0,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      outlineColorMix: {},
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isScale: false,
      isShowIndicator: true,
      colorMix: {
        "0": { color: "red", isHidden: false },
      },
      fill: "solid" as TLDefaultFillStyle,
      dash: "solid" as TLDefaultDashStyle,
    };
  }

  getGeometry(shape: TLHighlightShapeUtilProps) {
    const strokeWidth = getStrokeWidth(shape);
    if (getIsDot(shape)) {
      return new Circle2d({
        x: -strokeWidth / 2,
        y: -strokeWidth / 2,
        radius: strokeWidth / 2,
        isFilled: true,
      });
    }

    const { strokePoints, sw } = getHighlightStrokePoints(shape, strokeWidth, true);
    const opts = getHighlightFreehandSettings({ strokeWidth: sw, showAsComplete: true });
    setStrokePointRadii(strokePoints, opts);

    return new Polygon2d({
      points: getStrokeOutlinePoints(strokePoints, opts),
      isFilled: true,
    });
  }

  component(shape: TLHighlightShapeUtilProps) {
    const forceSolid = useHighlightForceSolid(this.editor, shape);
    const strokeWidth = getStrokeWidth(shape);
    const { outShadowArray, inShadowArray, colorMix, transparency } = shape.props;

    const allPointsFromSegments = getPointsFromSegments(shape.props.segments);

    let sw = strokeWidth;
    if (!forceSolid && !shape.props.isPen && allPointsFromSegments.length === 1) {
      sw += rng(shape.id)() * (sw / 6);
    }

    const options = getHighlightFreehandSettings({
      strokeWidth: sw,
      showAsComplete: shape.props.isComplete || last(shape.props.segments)?.type === "straight",
    });

    const strokePoints = getStrokePoints(allPointsFromSegments, options);

    const solidStrokePath =
      strokePoints.length > 1
        ? getSvgPathFromStrokePoints(strokePoints, false)
        : getShapeDot(shape.props.segments[0].points[0]);

    return (
      <SVGContainer>
        <defs>
          {/* Gradient definition */}
          {Object.values(colorMix).filter((c) => !c.isHidden).length > 0 && (
            <linearGradient id={`gradient-${shape.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
              {Object.values(colorMix)
                .filter((c) => !c.isHidden)
                .map((colorObj, index) => (
                  <stop
                    key={index}
                    offset={`${(index / (Object.values(colorMix).filter((c) => !c.isHidden).length - 1)) * 100}%`}
                    stopColor={colorObj.color}
                  />
                ))}
            </linearGradient>
          )}
          {/* Shadow filter definition */}
          {/* {outShadowArray &&
            Object.values(outShadowArray).map((shadow) => (
              <>
                {!shadow.isHidden && (
                  <filter key={shadow.id} id={shadow.id} x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow
                      dx={shadow.dx}
                      dy={shadow.dy}
                      stdDeviation={shadow.shadowBlur}
                      floodColor={shadow.color}
                      floodOpacity={shadow.opacity}
                    />
                  </filter>
                )}
              </>
            ))} */}
          {/* Inner shadow filter definition */}
          {/* {inShadowArray &&
            Object.values(inShadowArray).map((shadow) => (
              <>
                {!shadow.isHidden && (
                  <filter key={shadow.id} id={shadow.id} x="-50%" y="-50%" width="200%" height="200%">
                    <feComponentTransfer in="SourceAlpha">
                      <feFuncA type="table" tableValues="1 0" />
                    </feComponentTransfer>
                    <feGaussianBlur stdDeviation={shadow.shadowBlur} />
                    <feOffset dx={shadow.dx} dy={shadow.dy} result="offsetBlur" />
                    <feFlood floodColor={shadow.color} result="color" />
                    <feComposite in="color" in2="offsetBlur" operator="in" />
                    <feComponentTransfer>
                      <feFuncA type="linear" slope={shadow.opacity} />
                    </feComponentTransfer>
                    <feComposite in2="SourceAlpha" operator="in" />
                    <feComposite operator="over" in2="SourceGraphic" />
                  </filter>
                )}
              </>
            ))} */}
        </defs>
        <HighlightRenderer
          shape={shape}
          forceSolid={forceSolid}
          strokeWidth={strokeWidth}
          opacity={transparency}
          solidStrokePath={solidStrokePath}
          sw={sw}
        />
      </SVGContainer>
    );
  }

  backgroundComponent(shape: TLHighlightShapeUtilProps) {
    const forceSolid = useHighlightForceSolid(this.editor, shape);
    const strokeWidth = getStrokeWidth(shape);

    const visibleColors = Object.values(shape.props.colorMix).filter((c) => !c.isHidden);
    const calculatedFill = visibleColors.length > 0 ? mixColors(visibleColors.map((c) => c.color)) : "transparent";

    const allPointsFromSegments = getPointsFromSegments(shape.props.segments);

    let sw = strokeWidth;
    if (!forceSolid && !shape.props.isPen && allPointsFromSegments.length === 1) {
      sw += rng(shape.id)() * (sw / 6);
    }

    const options = getHighlightFreehandSettings({
      strokeWidth: sw,
      showAsComplete: shape.props.isComplete || last(shape.props.segments)?.type === "straight",
    });

    const strokePoints = getStrokePoints(allPointsFromSegments, options);

    const solidStrokePath =
      strokePoints.length > 1
        ? getSvgPathFromStrokePoints(strokePoints, false)
        : getShapeDot(shape.props.segments[0].points[0]);

    const { outShadowArray, inShadowArray, colorMix } = shape.props;
    return (
      <SVGContainer>
        <defs>
          {/* Gradient definition */}
          {Object.values(colorMix).filter((c) => !c.isHidden).length > 0 && (
            <linearGradient id={`gradient-${shape.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
              {Object.values(colorMix)
                .filter((c) => !c.isHidden)
                .map((colorObj, index) => (
                  <stop
                    key={index}
                    offset={`${(index / (Object.values(colorMix).filter((c) => !c.isHidden).length - 1)) * 100}%`}
                    stopColor={colorObj.color}
                  />
                ))}
            </linearGradient>
          )}
          {/* Shadow filter definition */}
          {outShadowArray &&
            Object.values(outShadowArray).map((shadow) => (
              <>
                {!shadow.isHidden && (
                  <filter key={shadow.id} id={shadow.id} x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow
                      dx={shadow.dx}
                      dy={shadow.dy}
                      stdDeviation={shadow.shadowBlur}
                      floodColor={shadow.color}
                      floodOpacity={shadow.opacity}
                    />
                  </filter>
                )}
              </>
            ))}
          {/* Inner shadow filter definition */}
          {inShadowArray &&
            Object.values(inShadowArray).map((shadow) => (
              <>
                {!shadow.isHidden && (
                  <filter key={shadow.id} id={shadow.id} x="-50%" y="-50%" width="200%" height="200%">
                    <feComponentTransfer in="SourceAlpha">
                      <feFuncA type="table" tableValues="1 0" />
                    </feComponentTransfer>
                    <feGaussianBlur stdDeviation={shadow.shadowBlur} />
                    <feOffset dx={shadow.dx} dy={shadow.dy} result="offsetBlur" />
                    <feFlood floodColor={shadow.color} result="color" />
                    <feComposite in="color" in2="offsetBlur" operator="in" />
                    <feComponentTransfer>
                      <feFuncA type="linear" slope={shadow.opacity} />
                    </feComponentTransfer>
                    <feComposite in2="SourceAlpha" operator="in" />
                    <feComposite operator="over" in2="SourceGraphic" />
                  </filter>
                )}
              </>
            ))}
        </defs>
        <HighlightRenderer
          shape={shape}
          forceSolid={forceSolid}
          strokeWidth={strokeWidth}
          opacity={this.options.underlayOpacity}
          solidStrokePath={solidStrokePath}
          sw={sw}
        />
        {/* {outShadowArray &&
          Object.values(outShadowArray).map((shadow) => (
            <path
              key={shadow.id}
              d={solidStrokePath}
              fill={calculatedFill}
              // stroke={outlineColor}
              strokeWidth={strokeWidth}
              // strokeDasharray={dashArray}
              // fillOpacity={transparency}
              // strokeOpacity={transparency}
              filter={`url(#${shadow.id})`}
            />
          ))}
        {inShadowArray &&
          Object.values(inShadowArray).map((shadow) => (
            <path
              key={shadow.id}
              d={solidStrokePath}
              fill={calculatedFill}
              // stroke={outlineColor}
              strokeWidth={strokeWidth}
              // strokeDasharray={dashArray}
              // fillOpacity={transparency}
              // strokeOpacity={transparency}
              filter={`url(#${shadow.id})`}
            />
          ))} */}
      </SVGContainer>
    );
  }

  indicator(shape: TLHighlightShapeUtilProps) {
    const forceSolid = useHighlightForceSolid(this.editor, shape);
    const strokeWidth = getStrokeWidth(shape);

    const { strokePoints, sw } = getHighlightStrokePoints(shape, strokeWidth, forceSolid);
    const allPointsFromSegments = getPointsFromSegments(shape.props.segments);

    let strokePath;
    if (strokePoints.length < 2) {
      strokePath = getIndicatorDot(allPointsFromSegments[0], sw);
    } else {
      strokePath = getSvgPathFromStrokePoints(strokePoints, false);
    }

    return !shape.props.isShowIndicator ? <path d={strokePath} /> : null;
  }

  toSvg(shape: TLHighlightShapeUtilProps) {
    const strokeWidth = getStrokeWidth(shape);
    const forceSolid = strokeWidth < 1.5;
    const scaleFactor = 1 / shape.props.scale;

    const allPointsFromSegments = getPointsFromSegments(shape.props.segments);

    let sw = strokeWidth;
    if (!forceSolid && !shape.props.isPen && allPointsFromSegments.length === 1) {
      sw += rng(shape.id)() * (sw / 6);
    }

    const options = getHighlightFreehandSettings({
      strokeWidth: sw,
      showAsComplete: shape.props.isComplete || last(shape.props.segments)?.type === "straight",
    });

    const strokePoints = getStrokePoints(allPointsFromSegments, options);

    const solidStrokePath =
      strokePoints.length > 1
        ? getSvgPathFromStrokePoints(strokePoints, false)
        : getShapeDot(shape.props.segments[0].points[0]);

    return (
      <g transform={`scale(${scaleFactor})`}>
        <HighlightRenderer
          forceSolid={forceSolid}
          strokeWidth={strokeWidth}
          shape={shape}
          opacity={shape.props.transparency}
          solidStrokePath={solidStrokePath}
          sw={sw}
        />
      </g>
    );
  }

  toBackgroundSvg(shape: TLHighlightShapeUtilProps) {
    const strokeWidth = getStrokeWidth(shape);
    const forceSolid = strokeWidth < 1.5;
    const scaleFactor = 1 / shape.props.scale;

    const allPointsFromSegments = getPointsFromSegments(shape.props.segments);

    let sw = strokeWidth;
    if (!forceSolid && !shape.props.isPen && allPointsFromSegments.length === 1) {
      sw += rng(shape.id)() * (sw / 6);
    }

    const options = getHighlightFreehandSettings({
      strokeWidth: sw,
      showAsComplete: shape.props.isComplete || last(shape.props.segments)?.type === "straight",
    });

    const strokePoints = getStrokePoints(allPointsFromSegments, options);

    const solidStrokePath =
      strokePoints.length > 1
        ? getSvgPathFromStrokePoints(strokePoints, false)
        : getShapeDot(shape.props.segments[0].points[0]);

    return (
      <g transform={`scale(${scaleFactor})`}>
        <HighlightRenderer
          forceSolid={forceSolid}
          strokeWidth={strokeWidth}
          shape={shape}
          opacity={this.options.underlayOpacity}
          solidStrokePath={solidStrokePath}
          sw={sw}
        />
      </g>
    );
  }

  onResize(shape: TLHighlightShapeUtilProps, info: TLResizeInfo<TLHighlightShapeUtilProps>) {
    const { scaleX, scaleY } = info;

    const newSegments: TLDrawShapeSegment[] = [];

    for (const segment of shape.props.segments) {
      newSegments.push({
        ...segment,
        points: segment.points.map(({ x, y, z }) => {
          return {
            x: scaleX * x,
            y: scaleY * y,
            z,
          };
        }),
      });
    }

    return {
      props: {
        segments: newSegments,
      },
    };
  }

  getInterpolatedProps(
    startShape: TLHighlightShapeUtilProps,
    endShape: TLHighlightShapeUtilProps,
    t: number
  ): TLHighlightShapeUtilProps["props"] {
    return {
      ...(t > 0.5 ? endShape.props : startShape.props),
      ...endShape.props,
      segments: interpolateSegments(startShape.props.segments, endShape.props.segments, t),
      scale: lerp(startShape.props.scale, endShape.props.scale, t),
    };
  }
}

function getShapeDot(point: VecLike) {
  const r = 0.1;
  return `M ${point.x} ${point.y} m -${r}, 0 a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 -${r * 2},0`;
}

function getIndicatorDot(point: VecLike, sw: number) {
  const r = sw / 2;
  return `M ${point.x} ${point.y} m -${r}, 0 a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 -${r * 2},0`;
}

function getHighlightStrokePoints(shape: TLHighlightShapeUtilProps, strokeWidth: number, forceSolid: boolean) {
  const allPointsFromSegments = getPointsFromSegments(shape.props.segments);
  const showAsComplete = shape.props.isComplete || last(shape.props.segments)?.type === "straight";

  let sw = strokeWidth;
  if (!forceSolid && !shape.props.isPen && allPointsFromSegments.length === 1) {
    sw += rng(shape.id)() * (strokeWidth / 6);
  }

  const options = getHighlightFreehandSettings({
    strokeWidth: sw,
    showAsComplete,
  });

  const strokePoints = getStrokePoints(allPointsFromSegments, options);

  return { strokePoints, sw };
}

function getStrokeWidth(shape: TLHighlightShapeUtilProps) {
  // Use weight if available, otherwise use default size calculation
  if (shape.props.weight !== undefined) {
    return shape.props.weight * shape.props.scale;
  }
  return FONT_SIZES[shape.props.size] * 1.12 * shape.props.scale;
}

function getIsDot(shape: TLHighlightShapeUtilProps) {
  return shape.props.segments.length === 1 && shape.props.segments[0].points.length < 2;
}

function HighlightRenderer({
  sw,
  shape,
  opacity,
  solidStrokePath,
}: {
  sw: number;
  strokeWidth: number;
  forceSolid: boolean;
  shape: TLHighlightShapeUtilProps;
  opacity: number;
  solidStrokePath: any;
}) {
  const theme = useDefaultColorTheme();

  // const colorSpace = useColorSpace();
  // const color = theme[shape.props.color].customHighlight[colorSpace];

  const visibleColors = Object.values(shape.props.colorMix).filter((c) => !c.isHidden);
  const calculatedFill = visibleColors.length > 0 ? mixColors(visibleColors.map((c) => c.color)) : "transparent";

  const shadowFilterId = `shadow-${shape.id}`; // Unique ID for the shadow filter
  const innerShadowFilterId = `inner-shadow-${shape.id}`;
  const dashArray = shape.props.strokeDasharray || getDashArray(shape.props.dash);
  return (
    <path
      d={solidStrokePath}
      strokeLinecap="round"
      fill="none"
      pointerEvents="all"
      stroke={calculatedFill}
      strokeWidth={sw}
      opacity={opacity}
      strokeDasharray={dashArray}
      filter={`url(#${shadowFilterId})`}
    />
  );
}

function useHighlightForceSolid(editor: Editor, shape: TLHighlightShapeUtilProps) {
  return useValue(
    "forceSolid",
    () => {
      const sw = getStrokeWidth(shape);
      const zoomLevel = editor.getZoomLevel();
      if (sw / zoomLevel < 1.5) {
        return true;
      }
      return false;
    },
    [editor]
  );
}

//////////////////////////////////////////////////////////////////
const { min } = Math;

// This is the rate of change for simulated pressure. It could be an option.
const RATE_OF_PRESSURE_CHANGE = 0.275;
/** @public */
export function setStrokePointRadii(strokePoints: StrokePoint[], options: StrokeOptions) {
  const { size = 16, thinning = 0.5, simulatePressure = true, easing = (t) => t, start = {}, end = {} } = options;

  const { easing: taperStartEase = EASINGS.easeOutQuad } = start;
  const { easing: taperEndEase = EASINGS.easeOutCubic } = end;

  const totalLength = strokePoints[strokePoints.length - 1].runningLength;

  let firstRadius: number | undefined;
  let prevPressure = strokePoints[0].pressure;
  let strokePoint: StrokePoint;

  if (!simulatePressure && totalLength < size) {
    const max = strokePoints.reduce((max, curr) => Math.max(max, curr.pressure), 0.5);
    strokePoints.forEach((sp) => {
      sp.pressure = max;
      sp.radius = size * easing(0.5 - thinning * (0.5 - sp.pressure));
    });
    return strokePoints;
  } else {
    // Calculate initial pressure based on the average of the first
    // n number of points. This prevents "dots" at the start of the
    // line. Drawn lines almost always start slow!
    let p: number;
    for (let i = 0, n = strokePoints.length; i < n; i++) {
      strokePoint = strokePoints[i];
      if (strokePoint.runningLength > size * 5) break;
      const sp = min(1, strokePoint.distance / size);
      if (simulatePressure) {
        const rp = min(1, 1 - sp);
        p = min(1, prevPressure + (rp - prevPressure) * (sp * RATE_OF_PRESSURE_CHANGE));
      } else {
        p = min(1, prevPressure + (strokePoint.pressure - prevPressure) * 0.5);
      }
      prevPressure = prevPressure + (p - prevPressure) * 0.5;
    }

    // Now calculate pressure and radius for each point
    for (let i = 0; i < strokePoints.length; i++) {
      strokePoint = strokePoints[i];
      if (thinning) {
        let { pressure } = strokePoint;
        const sp = min(1, strokePoint.distance / size);
        if (simulatePressure) {
          // If we're simulating pressure, then do so based on the distance
          // between the current point and the previous point, and the size
          // of the stroke.
          const rp = min(1, 1 - sp);
          pressure = min(1, prevPressure + (rp - prevPressure) * (sp * RATE_OF_PRESSURE_CHANGE));
        } else {
          // Otherwise, use the input pressure slightly smoothed based on the
          // distance between the current point and the previous point.
          pressure = min(1, prevPressure + (pressure - prevPressure) * (sp * RATE_OF_PRESSURE_CHANGE));
        }

        strokePoint.radius = size * easing(0.5 - thinning * (0.5 - pressure));

        prevPressure = pressure;
      } else {
        strokePoint.radius = size / 2;
      }

      if (firstRadius === undefined) {
        firstRadius = strokePoint.radius;
      }
    }
  }

  const taperStart =
    start.taper === false ? 0 : start.taper === true ? Math.max(size, totalLength) : (start.taper as number);

  const taperEnd = end.taper === false ? 0 : end.taper === true ? Math.max(size, totalLength) : (end.taper as number);

  if (taperStart || taperEnd) {
    for (let i = 0; i < strokePoints.length; i++) {
      strokePoint = strokePoints[i];
      /*
				Apply tapering

				If the current length is within the taper distance at either the
				start or the end, calculate the taper strengths. Apply the smaller 
				of the two taper strengths to the radius.
			*/

      const { runningLength } = strokePoint;

      const ts = runningLength < taperStart ? taperStartEase(runningLength / taperStart) : 1;

      const te = totalLength - runningLength < taperEnd ? taperEndEase((totalLength - runningLength) / taperEnd) : 1;

      strokePoint.radius = Math.max(0.01, strokePoint.radius * Math.min(ts, te));
    }
  }

  return strokePoints;
}

/////////////////////////////////////////////////////////////////////////
const { PI } = Math;

// Browser strokes seem to be off if PI is regular, a tiny offset seems to fix it
const FIXED_PI = PI + 0.0001;

/**
 * @internal
 */
export function getStrokeOutlineTracks(
  strokePoints: StrokePoint[],
  options: StrokeOptions = {}
): { left: Vec[]; right: Vec[] } {
  const { size = 16, smoothing = 0.5 } = options;

  // We can't do anything with an empty array or a stroke with negative size.
  if (strokePoints.length === 0 || size <= 0) {
    return { left: [], right: [] };
  }

  const firstStrokePoint = strokePoints[0];
  const lastStrokePoint = strokePoints[strokePoints.length - 1];

  // The total length of the line
  const totalLength = lastStrokePoint.runningLength;

  // The minimum allowed distance between points (squared)
  const minDistance = Math.pow(size * smoothing, 2);

  // Our collected left and right points
  const leftPts: Vec[] = [];
  const rightPts: Vec[] = [];

  // Previous vector
  let prevVector = strokePoints[0].vector;

  // Previous left and right points
  let pl = strokePoints[0].point;
  let pr = pl;

  // Temporary left and right points
  let tl = pl;
  let tr = pr;

  // Keep track of whether the previous point is a sharp corner
  // ... so that we don't detect the same corner twice
  let isPrevPointSharpCorner = false;

  /*
    Find the outline's left and right points

    Iterating through the points and populate the rightPts and leftPts arrays,
    skipping the first and last pointsm, which will get caps later on.
  */

  let strokePoint: StrokePoint;

  for (let i = 0; i < strokePoints.length; i++) {
    strokePoint = strokePoints[i];
    const { point, vector } = strokePoints[i];

    /*
      Handle sharp corners

      Find the difference (dot product) between the current and next vector.
      If the next vector is at more than a right angle to the current vector,
      customDraw a cap at the current point.
    */

    const prevDpr = strokePoint.vector.dpr(prevVector);
    const nextVector = (i < strokePoints.length - 1 ? strokePoints[i + 1] : strokePoints[i]).vector;
    const nextDpr = i < strokePoints.length - 1 ? nextVector.dpr(strokePoint.vector) : 1;

    const isPointSharpCorner = prevDpr < 0 && !isPrevPointSharpCorner;
    const isNextPointSharpCorner = nextDpr !== null && nextDpr < 0.2;

    if (isPointSharpCorner || isNextPointSharpCorner) {
      // It's a sharp corner. Draw a rounded cap and move on to the next point
      // Considering saving these and drawing them later? So that we can avoid
      // crossing future points.

      if (nextDpr > -0.62 && totalLength - strokePoint.runningLength > strokePoint.radius) {
        // Draw a "soft" corner
        const offset = prevVector.clone().mul(strokePoint.radius);
        const cpr = prevVector.clone().cpr(nextVector);

        if (cpr < 0) {
          tl = Vec.Add(point, offset);
          tr = Vec.Sub(point, offset);
        } else {
          tl = Vec.Sub(point, offset);
          tr = Vec.Add(point, offset);
        }

        leftPts.push(tl);
        rightPts.push(tr);
      } else {
        // Draw a "sharp" corner
        const offset = prevVector.clone().mul(strokePoint.radius).per();
        const start = Vec.Sub(strokePoint.input, offset);

        for (let step = 1 / 13, t = 0; t < 1; t += step) {
          tl = Vec.RotWith(start, strokePoint.input, FIXED_PI * t);
          leftPts.push(tl);

          tr = Vec.RotWith(start, strokePoint.input, FIXED_PI + FIXED_PI * -t);
          rightPts.push(tr);
        }
      }

      pl = tl;
      pr = tr;

      if (isNextPointSharpCorner) {
        isPrevPointSharpCorner = true;
      }

      continue;
    }

    isPrevPointSharpCorner = false;

    if (strokePoint === firstStrokePoint || strokePoint === lastStrokePoint) {
      const offset = Vec.Per(vector).mul(strokePoint.radius);
      leftPts.push(Vec.Sub(point, offset));
      rightPts.push(Vec.Add(point, offset));

      continue;
    }

    /* 
      Add regular points

      Project points to either side of the current point, using the
      calculated size as a distance. If a point's distance to the 
      previous point on that side greater than the minimum distance
      (or if the corner is kinda sharp), add the points to the side's
      points array.
    */

    const offset = Vec.Lrp(nextVector, vector, nextDpr).per().mul(strokePoint.radius);

    tl = Vec.Sub(point, offset);

    if (i <= 1 || Vec.Dist2(pl, tl) > minDistance) {
      leftPts.push(tl);
      pl = tl;
    }

    tr = Vec.Add(point, offset);

    if (i <= 1 || Vec.Dist2(pr, tr) > minDistance) {
      rightPts.push(tr);
      pr = tr;
    }

    // Set variables for next iteration
    prevVector = vector;

    continue;
  }

  /*
    Return the points in the correct winding order: begin on the left side, then 
    continue around the end cap, then come back along the right side, and finally 
    complete the start cap.
  */

  return {
    left: leftPts,
    right: rightPts,
  };
}

/**
 * ## getStrokeOutlinePoints
 *
 * Get an array of points (as `[x, y]`) representing the outline of a stroke.
 *
 * @param points - An array of StrokePoints as returned from `getStrokePoints`.
 * @param options - An object with options.
 * @public
 */
export function getStrokeOutlinePoints(strokePoints: StrokePoint[], options: StrokeOptions = {}): Vec[] {
  const { size = 16, start = {}, end = {}, last: isComplete = false } = options;

  const { cap: capStart = true } = start;
  const { cap: capEnd = true } = end;

  // We can't do anything with an empty array or a stroke with negative size.
  if (strokePoints.length === 0 || size <= 0) {
    return [];
  }

  const firstStrokePoint = strokePoints[0];
  const lastStrokePoint = strokePoints[strokePoints.length - 1];

  // The total length of the line
  const totalLength = lastStrokePoint.runningLength;

  const taperStart =
    start.taper === false ? 0 : start.taper === true ? Math.max(size, totalLength) : (start.taper as number);

  const taperEnd = end.taper === false ? 0 : end.taper === true ? Math.max(size, totalLength) : (end.taper as number);

  // The minimum allowed distance between points (squared)
  // Our collected left and right points
  const { left: leftPts, right: rightPts } = getStrokeOutlineTracks(strokePoints, options);

  /*
    Drawing caps
    
    Now that we have our points on either side of the line, we need to
    customDraw caps at the start and end. Tapered lines don't have caps, but
    may have dots for very short lines.
  */

  const firstPoint = firstStrokePoint.point;

  const lastPoint =
    strokePoints.length > 1 ? strokePoints[strokePoints.length - 1].point : Vec.AddXY(firstStrokePoint.point, 1, 1);

  /* 
    Draw a dot for very short or completed strokes
    
    If the line is too short to gather left or right points and if the line is
    not tapered on either side, customDraw a dot. If the line is tapered, then only
    customDraw a dot if the line is both very short and complete. If we customDraw a dot,
    we can just return those points.
  */

  if (strokePoints.length === 1) {
    if (!(taperStart || taperEnd) || isComplete) {
      const start = Vec.Add(firstPoint, Vec.Sub(firstPoint, lastPoint).uni().per().mul(-firstStrokePoint.radius));
      const dotPts: Vec[] = [];
      for (let step = 1 / 13, t = step; t <= 1; t += step) {
        dotPts.push(Vec.RotWith(start, firstPoint, FIXED_PI * 2 * t));
      }
      return dotPts;
    }
  }

  /*
    Draw a start cap

    Unless the line has a tapered start, or unless the line has a tapered end
    and the line is very short, customDraw a start cap around the first point. Use
    the distance between the second left and right point for the cap's radius.
    Finally remove the first left and right points. :psyduck:
  */

  const startCap: Vec[] = [];
  if (taperStart || (taperEnd && strokePoints.length === 1)) {
    // The start point is tapered, noop
  } else if (capStart) {
    // Draw the round cap - add thirteen points rotating the right point around the start point to the left point
    for (let step = 1 / 8, t = step; t <= 1; t += step) {
      const pt = Vec.RotWith(rightPts[0], firstPoint, FIXED_PI * t);
      startCap.push(pt);
    }
  } else {
    // Draw the flat cap - add a point to the left and right of the start point
    const cornersVector = Vec.Sub(leftPts[0], rightPts[0]);
    const offsetA = Vec.Mul(cornersVector, 0.5);
    const offsetB = Vec.Mul(cornersVector, 0.51);

    startCap.push(
      Vec.Sub(firstPoint, offsetA),
      Vec.Sub(firstPoint, offsetB),
      Vec.Add(firstPoint, offsetB),
      Vec.Add(firstPoint, offsetA)
    );
  }

  /*
    Draw an end cap

    If the line does not have a tapered end, and unless the line has a tapered
    start and the line is very short, customDraw a cap around the last point. Finally,
    remove the last left and right points. Otherwise, add the last point. Note
    that This cap is a full-turn-and-a-half: this prevents incorrect caps on
    sharp end turns.
  */

  const endCap: Vec[] = [];
  const direction = lastStrokePoint.vector.clone().per().neg();

  if (taperEnd || (taperStart && strokePoints.length === 1)) {
    // Tapered end - push the last point to the line
    endCap.push(lastPoint);
  } else if (capEnd) {
    // Draw the round end cap
    const start = Vec.Add(lastPoint, Vec.Mul(direction, lastStrokePoint.radius));
    for (let step = 1 / 29, t = step; t < 1; t += step) {
      endCap.push(Vec.RotWith(start, lastPoint, FIXED_PI * 3 * t));
    }
  } else {
    // Draw the flat end cap
    endCap.push(
      Vec.Add(lastPoint, Vec.Mul(direction, lastStrokePoint.radius)),
      Vec.Add(lastPoint, Vec.Mul(direction, lastStrokePoint.radius * 0.99)),
      Vec.Sub(lastPoint, Vec.Mul(direction, lastStrokePoint.radius * 0.99)),
      Vec.Sub(lastPoint, Vec.Mul(direction, lastStrokePoint.radius))
    );
  }

  /*
    Return the points in the correct winding order: begin on the left side, then 
    continue around the end cap, then come back along the right side, and finally 
    complete the start cap.
  */

  return leftPts.concat(endCap, rightPts.reverse(), startCap);
}

/** @public */
export const interpolateSegments = (
  startSegments: TLDrawShapeSegment[],
  endSegments: TLDrawShapeSegment[],
  progress: number
): TLDrawShapeSegment[] => {
  const startPoints: VecModel[] = [];
  const endPoints: VecModel[] = [];

  // Extract all points from startSegments and endSegments
  startSegments.forEach((segment) => startPoints.push(...segment.points));
  endSegments.forEach((segment) => endPoints.push(...segment.points));

  const maxLength = Math.max(startPoints.length, endPoints.length);
  const pointsToUseStart: VecModel[] = [];
  const pointsToUseEnd: VecModel[] = [];

  // Ensure both arrays have the same length
  for (let i = 0; i < maxLength; i++) {
    pointsToUseStart.push(startPoints[i] || startPoints[startPoints.length - 1]);
    pointsToUseEnd.push(endPoints[i] || endPoints[endPoints.length - 1]);
  }

  // Interpolate points
  const interpolatedPoints = pointsToUseStart.map((point, k) => {
    let z = 0.5;
    if (pointsToUseEnd[k].z !== undefined && point.z !== undefined) {
      z = lerp(point.z, pointsToUseEnd[k].z as number, progress);
    }
    return {
      x: lerp(point.x, pointsToUseEnd[k].x, progress),
      y: lerp(point.y, pointsToUseEnd[k].y, progress),
      z,
    };
  });
  // Return all interpolated points in a single segment
  return [
    {
      type: "free",
      points: interpolatedPoints,
    },
  ];
};

//////////////////////////////////////////////////////////////////

export function useColorSpace(): "srgb" | "p3" {
  const [supportsP3, setSupportsP3] = useState(false);

  useEffect(() => {
    const supportsSyntax = CSS.supports("color", "color(display-p3 1 1 1)");
    const query = matchMedia("(color-gamut: p3)");
    setSupportsP3(supportsSyntax && query.matches);

    const onChange = () => setSupportsP3(supportsSyntax && query.matches);

    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  //   const forceSrgb = useValue(debugFlags.forceSrgb);

  return !supportsP3 ? "srgb" : "p3";
}
